import { NgClass } from "@angular/common";
import { ChangeDetectorRef, Component, DestroyRef, inject, input, OnDestroy, OnInit, output } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { DrawMode, PolyDrawService, PolygonInformationService, PolygonState } from "polydraw";
import { debounceTime, Subscription } from "rxjs";
import { AreaBuilderDataService } from "src/services/area-builder-data.service";
import { InternalNavigationService } from "src/services/internal-navigation.service";
import { RightSidebarOptions, RightSidebarService } from "src/services/right-sidebar.service";
import { AreasStore } from "src/stores/area-builder.store";
import { EvTooltipSimpleComponent } from "../ev-tooltip-simple/ev-tooltip-simple.component";

@Component({
    selector: "freedraw-buttons",
    templateUrl: "./freedraw-buttons.component.html",
    styleUrl: "./freedraw-buttons.component.scss",
    imports: [NgClass, EvTooltipSimpleComponent],
})
export class FreedrawButtonsComponent implements OnInit, OnDestroy {
    canShow = input(false);
    mobileMode = input(false);
    onRevert = output<void>();

    polygonState: PolygonState;
    destroyRef = inject(DestroyRef);
    isAreaBuilderActive: boolean;
    mapHelperService = inject(PolyDrawService);
    drawService = inject(PolygonInformationService);
    activatedRoute = inject(ActivatedRoute);
    areaBuilderService = inject(AreaBuilderDataService);
    navigationService = inject(InternalNavigationService);
    cdr = inject(ChangeDetectorRef);
    rightSidebarOptions = RightSidebarOptions;
    private readonly rightSidebarService = inject(RightSidebarService);
    readonly areasStore = inject(AreasStore);
    private subscriptions: Subscription[] = [];

    ngOnInit() {
        this.subscriptions.push(
            this.drawService.polygonState$.pipe(debounceTime(100)).subscribe((state: PolygonState) => {
                this.polygonStateHandler(state);
                if (!state?.drawStates.canUsePolyDraw && state?.drawStates.isActivated) {
                    this.mapHelperService.setDrawMode(DrawMode.Off);
                }
            }),
        );
        this.activatedRoute.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: ParamMap) => {
            if (params.has("areabuilderid") && params.get("areabuilderid") !== "-1") {
                this.isAreaBuilderActive = true;
            } else {
                this.isAreaBuilderActive = false;
            }
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    onFreedrawMenuClick(): void {
        if (this.polygonState?.drawStates.canUsePolyDraw) {
            this.mapHelperService.freedrawMenuClick();
            this.mapHelperService.setDrawMode(DrawMode.Add);
        }
    }

    onDrawModeClick(): void {
        if (this.polygonState?.drawStates.canUsePolyDraw) {
            this.mapHelperService.drawModeClick();
        }
    }

    onRevertClick(): void {
        this.mapHelperService.removeAllFeatureGroups();
        this.mapHelperService.setDrawMode(DrawMode.Off);
        this.onRevert.emit();
        this.areasStore.removeAllSelectedAreas() 
    }

    onAreaBuilderClick(): void {
        this.rightSidebarService.toggleSidebarVisibility(this.rightSidebarOptions.AreaBuilder);
    }

    private polygonStateHandler(polygonState: PolygonState): void {
        this.polygonState = polygonState;
        this.cdr.detectChanges();
    }
}
